import axios from "axios";

// Bare axios instance — no Firebase interceptor. The partner-view flow is
// cookie-authenticated end-to-end (the exchange handler sets a Secure /
// HttpOnly / SameSite=Strict cookie scoped to /public/partner-view/), so
// no Authorization header should ever ride along. Using a fresh instance
// also keeps a logged-in Sigo admin's Firebase token from leaking into
// partner-view requests during the "Preview as partner" flow.
const partnerViewClient = axios.create({
  responseType: "json",
  withCredentials: true,
});

/**
 * Returned by /public/partner-view/exchange. Flat shape — no Superset URL
 * because the partner-view shell doesn't need it; supersetUrl is delivered
 * by analytics_config once the embed actually mounts.
 */
export interface PartnerViewExchangeResponse {
  partner_id: number;
  partner_name: string;
  superset_dashboard_id: string;
  label: string | null;
}

export interface PartnerViewAnalyticsConfig {
  guest_token: string;
  superset_url: string;
  superset_dashboard_id: string;
  /** Actual native time-filter id; null means the linked board has none.
   * Optional only for compatibility with an older backend during rollout. */
  time_filter_id?: string | null;
  // ISO-8601 timestamp the snapshot is current through (MAX(materialized_at)
  // table-wide), or null if the table is empty.
  data_snapshot_at: string | null;
}

/**
 * Exchanges a magic-link token for a session cookie. Token is taken from the
 * URL fragment, never the path or query, so it does not appear in server,
 * proxy, or CDN access logs. The backend issues a Set-Cookie scoped to
 * /public/partner-view/ on success.
 *
 * On error the backend returns `{"error": {"message": "..."}}` directly (no
 * envelope) with a 4xx/5xx status. Callers should classify on the status code.
 */
export async function exchangePartnerToken(
  token: string
): Promise<PartnerViewExchangeResponse> {
  const response = await partnerViewClient.post(
    "/public/partner-view/exchange",
    { token }
  );
  return response.data;
}

/**
 * Mints a Superset guest token for the partner's dashboard. Authentication
 * rides on the session cookie set by /exchange; partner_id is read from
 * the server-side session, never from the request body.
 */
export async function getPartnerViewAnalyticsConfig(): Promise<PartnerViewAnalyticsConfig> {
  const response = await partnerViewClient.post(
    "/public/partner-view/analytics_config",
    {}
  );
  return response.data;
}

export type PartnerViewExportFormat = "csv" | "xlsx";

/**
 * URL of the partner-view export endpoint. A same-origin GET to this URL
 * includes the SameSite=Strict session cookie scoped to /public/partner-view/,
 * so the browser handles the download (via the Content-Disposition header) and
 * the page does not navigate.
 */
export function getPartnerViewExportUrl(
  format: PartnerViewExportFormat
): string {
  return `/public/partner-view/export?format=${format}`;
}
